/**
 * The operator-facing control for the brand's own fallback thumbnails in `config.central.images`.
 *
 * It owns one key of that document — `game.fallback-images` — and hands the rewritten document back
 * to its parent, which owns the save. Each orientation stands on its own: one left empty, or not
 * serving an image yet, takes nothing away from the other.
 */
import { useEffect, useMemo, useState } from "react";
import { CardBox } from "../../RouterView";
import { t } from "../../../i18n";
import { AssetDimensions, AssetSample } from "./assetSample";
import {
  CentralImagesDocument,
  FALLBACK_ORIENTATIONS,
  FallbackImages,
  FallbackOrientation,
  fallbackImagesBasis,
  parseCentralImagesDocument,
  readCentralImagesEnabled,
  readDefaultStyle,
  readFallbackImages,
  readStyleSize,
  readStyleTemplate,
  ThumbnailStyle,
  writeFallbackImages,
} from "./centralImagesDocument";
import { FallbackImagesNotices } from "./FallbackImagesNotices";
import { resolveEnforcedStyleVerdict } from "./enforcedStyleVerdict";
import { AssetMeasurementOutcome, measureAssetSize } from "./measureAssetSize";

interface FallbackImagesControlProps {
  /** The raw `JsonDocument.json` text the parent is editing. */
  json: string;
  /** `JsonDocument.locked` — a locked document cannot be changed here. */
  locked: boolean;
  /** Receives the rewritten document text; the parent decides when it reaches the server. */
  onApply: (json: string) => void;
}

/** What the fields hold, which is the operator's text rather than the urls read out of it. */
type FallbackImagesDraft = Record<FallbackOrientation, string>;

/** A draft together with the document it was formed against. */
interface PinnedDraft {
  basis: string;
  draft: FallbackImagesDraft;
}

/** A reading together with the url it was taken of. */
interface PinnedMeasurement {
  url: string;
  outcome: AssetMeasurementOutcome;
}

type PinnedMeasurements = Partial<Record<FallbackOrientation, PinnedMeasurement>>;

/**
 * A fallback url names one image outright, unlike a style's template, so the single sample carries
 * nothing to substitute into it and `resolveAssetUrl` returns it with any literal $game, $studio or $studioname stripped out.
 */
const WHOLE_URL_SAMPLE: AssetSample[] = [{ game: "", studio: "" }];

const FIELD_COPY: Record<FallbackOrientation, () => string> = {
  landscape: () => t("enforced-thumbnail-style.fallback-landscape-label", undefined, "Landscape fallback image url"),
  portrait: () => t("enforced-thumbnail-style.fallback-portrait-label", undefined, "Portrait fallback image url"),
};

function draftFromDocument(fallbackImages: FallbackImages): FallbackImagesDraft {
  return Object.fromEntries(FALLBACK_ORIENTATIONS.map((orientation) => [orientation, fallbackImages[orientation] ?? ""])) as FallbackImagesDraft;
}

/**
 * Hosts a browser holds to be a secure context whatever scheme they are reached over, so an address
 * an operator is exercising from their own machine is not worth a notice about the players' lobby.
 * The brackets are the form `URL` reports a literal IPv6 host in, which is what is compared here.
 */
const SECURE_CONTEXT_HOSTS = ["localhost", "127.0.0.1", "[::1]"];

/**
 * Player lobbies are served over https, where an image addressed over plain http is refused by the
 * browser before any request leaves it. Nothing about that refusal reaches the operator: the tile
 * simply stands on Nano's artwork, and the address looks perfectly well formed in this field.
 * An address this cannot read at all is left to the reading, which reports what it finds.
 */
function servedOverPlainHttp(url: string): boolean {
  try {
    const { protocol, hostname } = new URL(url);
    return protocol === "http:" && !SECURE_CONTEXT_HOSTS.includes(hostname);
  } catch {
    return false;
  }
}

/** A square image suits neither orientation worse than the other, so only a decisive shape is worth raising. */
function contradictsOrientation(orientation: FallbackOrientation, dimensions: AssetDimensions): boolean {
  return orientation === "landscape" ? dimensions.height > dimensions.width : dimensions.width > dimensions.height;
}

/** Keyed statically per orientation: a key built from a variable is a key the translation check cannot see. */
const IN_PLAY_COPY: Record<FallbackOrientation, (style: ThumbnailStyle) => string> = {
  landscape: (style) =>
    t(
      "enforced-thumbnail-style.fallback-in-play-landscape",
      { style },
      `The enforced style ${style} is landscape, so the landscape image is the one in use. Portrait applies once a portrait style is enforced.`,
    ),
  portrait: (style) =>
    t(
      "enforced-thumbnail-style.fallback-in-play-portrait",
      { style },
      `The enforced style ${style} is portrait, so the portrait image is the one in use. Landscape applies once a landscape style is enforced.`,
    ),
};

/**
 * Which orientation the enforced style puts in play, or undefined while nothing is enforced.
 *
 * Only one orientation is ever drawn: every surface that renders the stand-in takes its shape from
 * the enforced style's size, so the field for the other one sits idle until a style of that shape is
 * enforced. Both fields stay on screen regardless, because a url the operator cannot see is one they
 * cannot correct or clear.
 *
 * The style half of the rule is delegated rather than restated. The master switch is not part of
 * that verdict — every consumer conjoins it separately — and it is required to be true rather than
 * merely not false: the server's field is a primitive that defaults to off, so a document that never
 * mentions it enforces nothing.
 */
function orientationInPlay(centralImages: CentralImagesDocument | null): { orientation: FallbackOrientation; style: ThumbnailStyle } | undefined {
  if (readCentralImagesEnabled(centralImages) !== true) {
    return undefined;
  }

  const style = readDefaultStyle(centralImages);
  const verdict = resolveEnforcedStyleVerdict({
    defaultStyle: style,
    size: style === undefined ? undefined : readStyleSize(centralImages, style),
    urlTemplate: style === undefined ? undefined : readStyleTemplate(centralImages, style),
  });

  if (verdict.kind !== "enforced") {
    return undefined;
  }

  return { orientation: verdict.width >= verdict.height ? "landscape" : "portrait", style: verdict.style };
}

export function FallbackImagesControl({ json, locked, onApply }: FallbackImagesControlProps): JSX.Element {
  const centralImages = useMemo(() => parseCentralImagesDocument(json), [json]);
  const documentUnreadable = centralImages === null;
  const basis = useMemo(() => fallbackImagesBasis(centralImages), [centralImages]);
  const recorded = useMemo(() => readFallbackImages(centralImages), [centralImages]);

  // Until the operator touches a field, the document is what the fields show; from the first change
  // on the draft is. Deriving rather than syncing keeps the parent's own edits to the raw json —
  // which arrive on every keystroke of its textarea — from wiping an address halfway typed here.
  const [pinnedDraft, setPinnedDraft] = useState<PinnedDraft | undefined>(undefined);
  const [measurements, setMeasurements] = useState<PinnedMeasurements>({});

  const current = pinnedDraft?.basis === basis ? pinnedDraft.draft : draftFromDocument(recorded);

  // What is read is what the document records, not what the fields hold: every prefix of an address
  // being typed is an address of its own, and reading those would aim a request at the brand's host
  // per keystroke and paint "could not be loaded" over an address still being written. Trimmed to
  // the address the fields resolve to, so a pasted url with stray whitespace still matches.
  const recordedLandscape = recorded.landscape?.trim();
  const recordedPortrait = recorded.portrait?.trim();

  useEffect(() => {
    // A reading outlives the address it was taken for — the image is given up to its own deadline to
    // arrive — so a document replaced while one is in flight must not be reported on by it.
    let abandoned = false;

    const measured: FallbackImages = { landscape: recordedLandscape, portrait: recordedPortrait };

    FALLBACK_ORIENTATIONS.forEach((orientation) => {
      const url = measured[orientation];
      if (locked || url === undefined) {
        return;
      }
      void measureAssetSize(url, WHOLE_URL_SAMPLE).then((outcome) => {
        if (!abandoned) {
          setMeasurements((previous) => ({ ...previous, [orientation]: { url, outcome } }));
        }
      });
    });

    return () => {
      abandoned = true;
    };
  }, [locked, recordedLandscape, recordedPortrait]);

  const urls = Object.fromEntries(
    FALLBACK_ORIENTATIONS.map((orientation) => {
      const trimmed = current[orientation].trim();
      return [orientation, trimmed === "" ? undefined : trimmed];
    }),
  ) as Record<FallbackOrientation, string | undefined>;

  /** The reading in force for an orientation: a published reading only while it is still of the address on screen. */
  const readingFor = (orientation: FallbackOrientation): AssetMeasurementOutcome | undefined => {
    const pinned = measurements[orientation];
    return pinned !== undefined && pinned.url === urls[orientation] ? pinned.outcome : undefined;
  };

  const inPlay = orientationInPlay(centralImages);
  const configured = FALLBACK_ORIENTATIONS.filter((orientation) => urls[orientation] !== undefined);
  const insecure = FALLBACK_ORIENTATIONS.filter((orientation) => {
    const url = urls[orientation];
    return url !== undefined && servedOverPlainHttp(url);
  });
  const unreachable = configured.filter((orientation) => readingFor(orientation)?.status === "blocked");
  const contradicted = configured.filter((orientation) => {
    const reading = readingFor(orientation);
    return reading?.status === "agreed" && contradictsOrientation(orientation, reading.dimensions);
  });

  const changeUrl = (orientation: FallbackOrientation, value: string): void => {
    setPinnedDraft((pinned) => ({ basis, draft: { ...(pinned?.basis === basis ? pinned.draft : current), [orientation]: value } }));
  };

  const applySelection = (): void => {
    const rewritten = writeFallbackImages(json, current);
    if (rewritten !== null) {
      onApply(rewritten);
    }
  };

  return (
    <CardBox title={t("enforced-thumbnail-style.fallback-title", undefined, "Fallback thumbnail images")}>
      <div data-testid="fallback-images-control">
        <p className="text-muted">
          {t(
            "enforced-thumbnail-style.fallback-description",
            undefined,
            "Shown in place of a game that has no thumbnail in the enforced style. The default artwork shows where you set none.",
          )}{" "}
          {inPlay !== undefined && <span data-testid="fallback-images-orientation-in-play">{IN_PLAY_COPY[inPlay.orientation](inPlay.style)}</span>}
        </p>

        {!documentUnreadable && (
          <>
            {FALLBACK_ORIENTATIONS.map((orientation) => (
              <div
                className="form-group"
                key={orientation}
              >
                <label htmlFor={`fallback-images-${orientation}`}>{FIELD_COPY[orientation]()}</label>
                <input
                  id={`fallback-images-${orientation}`}
                  type="url"
                  className="form-control"
                  data-testid={`fallback-images-${orientation}`}
                  disabled={locked}
                  value={current[orientation]}
                  onChange={(event) => changeUrl(orientation, event.target.value)}
                />
              </div>
            ))}

            <FallbackImagesNotices
              soleOrientation={configured.length === 1 ? configured[0] : undefined}
              insecure={insecure}
              unreachable={unreachable}
              contradicted={contradicted}
            />
          </>
        )}

        <button
          className="btn btn-primary"
          type="button"
          data-testid="fallback-images-apply"
          disabled={locked || documentUnreadable}
          onClick={applySelection}
        >
          {t("enforced-thumbnail-style.apply", undefined, "Apply to document")}
        </button>
      </div>
    </CardBox>
  );
}
